import {
  Box,
  Button,
  Typography,
  styled,
  Divider,
  Grid,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { FC } from "react";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { FilterState } from "../LoginActivity";

const StyledButton = styled(Button)({
  borderRadius: "4px",
});

interface IFiltersSection {
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  handleFilterClick: (filterType: keyof FilterState, value: string) => void;
  handleExportToCSV: () => void;
  handleDateChange: (type: "start" | "end", value: string) => void;
}

const FiltersSection: FC<IFiltersSection> = ({
  filters,
  setFilters,
  handleFilterClick,
  handleExportToCSV,
  handleDateChange,
}) => {
  const isMobile = useMediaQuery("(max-width:767px)");

  const quickFiltersOptions = [
    {
      label: "Last 24h",
      value: "24",
    },
    {
      label: "Last 7d",
      value: "7",
    },
    {
      label: "Last 30d",
      value: "30",
    },
  ];

  const deviceTypeOptions = [
    {
      label: "Mobile",
      value: "Mobile",
    },
    {
      label: "Regular",
      value: "Browser",
    },
  ];

  const formatDate = (date: string | Date | undefined) => {
    if (!date) return "";
    if (typeof date === "string") {
      return date.split(" ")[0];
    }
    if (date instanceof Date) {
      return date.toISOString().split("T")[0];
    }
    return "";
  };

  const todayDate = new Date().toISOString().split("T")[0];

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box 
        sx={{ py: 2, maxWidth: isMobile ? "calc(100vw - 48px)" : "none" }}
        id="filters_section_container"
        data-test-id="filters_section_container"
        aria-label="Filters Section"
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mb: 2,
          }}
          id="filters_section_export"
          data-test-id="filters_section_export"
          aria-label="Export Controls"
        >
          <StyledButton
            variant="outlined"
            sx={{ gap: 1 }}
            onClick={handleExportToCSV}
            id="filters_section_export_button"
            data-test-id="filters_section_export_button"
            aria-label="Export to CSV"
          >
            Export to CSV
          </StyledButton>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            border: "1px solid #E2E8F0",
            boxShadow: 1,
            borderRadius: "4px",
            padding: 2,
            gap: 2,
          }}
          id="filters_section_content"
          data-test-id="filters_section_content"
          aria-label="Filters Content"
        >
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: isMobile ? "column" : "row",
            }}
            id="filters_section_main"
            data-test-id="filters_section_main"
            aria-label="Main Filters"
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                width: "100%",
              }}
              id="filters_section_left"
              data-test-id="filters_section_left"
              aria-label="Left Filters"
            >
              <Box
                id="filters_section_activity"
                data-test-id="filters_section_activity"
                aria-label="Activity Type Filters"
              >
                <Typography sx={{ fontWeight: 500, mb: 1 }} id="filters_section_activity_title" data-test-id="filters_section_activity_title" aria-label="Activity Type">
                  Activity Type
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {["Login", "Logout", "Failed"].map((type) => (
                    <StyledButton
                      key={type}
                      variant={
                        filters?.activityType?.includes(type)
                          ? "contained"
                          : "outlined"
                      }
                      onClick={() => handleFilterClick("activityType", type)}
                      id={`filters_section_activity_${type.toLowerCase()}`}
                      data-test-id={`filters_section_activity_${type.toLowerCase()}`}
                      aria-label={`Filter by ${type} Activity`}
                    >
                      {type}
                    </StyledButton>
                  ))}
                </Box>
              </Box>

              <Box
                id="filters_section_device"
                data-test-id="filters_section_device"
                aria-label="Device Type Filters"
              >
                <Typography sx={{ fontWeight: 500, mb: 1 }} id="filters_section_device_title" data-test-id="filters_section_device_title" aria-label="Device Type">
                  Device Type
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {deviceTypeOptions?.map((type) => (
                    <StyledButton
                      key={type.label}
                      variant={
                        filters?.deviceType?.includes(type.value)
                          ? "contained"
                          : "outlined"
                      }
                      onClick={() => handleFilterClick("deviceType", type.value)}
                      id={`filters_section_device_${type.value.toLowerCase()}`}
                      data-test-id={`filters_section_device_${type.value.toLowerCase()}`}
                      aria-label={`Filter by ${type.label} Device`}
                    >
                      {type.label}
                    </StyledButton>
                  ))}
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                width: "100%",
              }}
              id="filters_section_right"
              data-test-id="filters_section_right"
              aria-label="Right Filters"
            >
              <Box
                id="filters_section_user"
                data-test-id="filters_section_user"
                aria-label="User Type Filters"
              >
                <Typography sx={{ fontWeight: 500, mb: 1 }} id="filters_section_user_title" data-test-id="filters_section_user_title" aria-label="User Type">
                  User Type
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {["Customer", "Agent", "Admin"].map((type) => (
                    <StyledButton
                      key={type}
                      variant={
                        filters?.userType?.includes(type)
                          ? "contained"
                          : "outlined"
                      }
                      onClick={() => handleFilterClick("userType", type)}
                      id={`filters_section_user_${type.toLowerCase()}`}
                      data-test-id={`filters_section_user_${type.toLowerCase()}`}
                      aria-label={`Filter by ${type} User`}
                    >
                      {type}
                    </StyledButton>
                  ))}
                </Box>
              </Box>
              <Box
                id="filters_section_browser"
                data-test-id="filters_section_browser"
                aria-label="Browser Type Filters"
              >
                <Typography sx={{ fontWeight: 500, mb: 1 }} id="filters_section_browser_title" data-test-id="filters_section_browser_title" aria-label="Browser Type">
                  Browser Type
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {["Safari", "Edge", "Chrome", "Other"].map((browser) => (
                    <StyledButton
                      key={browser}
                      variant={
                        filters?.browserType?.includes(browser)
                          ? "contained"
                          : "outlined"
                      }
                      onClick={() => handleFilterClick("browserType", browser)}
                      id={`filters_section_browser_${browser.toLowerCase()}`}
                      data-test-id={`filters_section_browser_${browser.toLowerCase()}`}
                      aria-label={`Filter by ${browser} Browser`}
                    >
                      {browser}
                    </StyledButton>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
          <Divider />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? "1rem" : "0",
            }}
            id="filters_section_date"
            data-test-id="filters_section_date"
            aria-label="Date Filters"
          >
            <Box
              id="filters_section_quick"
              data-test-id="filters_section_quick"
              aria-label="Quick Date Filters"
            >
              <Typography sx={{ fontWeight: 500, mb: 1 }} id="filters_section_quick_title" data-test-id="filters_section_quick_title" aria-label="Quick Filters">
                Quick Filters
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                {quickFiltersOptions?.map((filter) => (
                  <StyledButton
                    key={filter?.label}
                    variant={
                      filters?.quickFilter === filter?.value
                        ? "contained"
                        : "outlined"
                    }
                    onClick={() => {
                      if (filters?.startDate || filters?.endDate) {
                        handleFilterClick("quickFilter", filter?.value);
                        handleFilterClick("startDate", "");
                        handleFilterClick("endDate", "");
                      } else {
                        handleFilterClick("quickFilter", filter?.value);
                      }
                    }}
                    id={`filters_section_quick_${filter?.value}`}
                    data-test-id={`filters_section_quick_${filter?.value}`}
                    aria-label={`Filter by ${filter?.label}`}
                  >
                    {filter?.label}
                  </StyledButton>
                ))}
              </Box>
            </Box>

            <Box
              id="filters_section_custom"
              data-test-id="filters_section_custom"
              aria-label="Custom Date Range"
            >
              <Typography sx={{ fontWeight: 500, mb: 1 }} id="filters_section_custom_title" data-test-id="filters_section_custom_title" aria-label="Custom Range">
                Custom Range
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs>
                  <TextField
                    size="small"
                    fullWidth
                    type="date"
                    label="Start Date"
                    value={formatDate(filters.startDate)}
                    onChange={(e) => {
                      const selectedDate = e.target.value;
                      const formattedDateTime = `${selectedDate} 00:00:00`;
                      if (filters?.quickFilter) {
                        handleFilterClick("quickFilter", "");
                      }
                      handleDateChange("start", formattedDateTime);
                    }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      max: todayDate,
                    }}
                    id="filters_section_start_date"
                    data-test-id="filters_section_start_date"
                    aria-label="Start Date"
                  />
                </Grid>
                <Grid item>
                  <Typography>to</Typography>
                </Grid>

                <Grid item xs>
                  <TextField
                    size="small"
                    fullWidth
                    type="date"
                    label="End Date"
                    value={formatDate(filters.endDate)}
                    onChange={(e) => {
                      const selectedDate = e.target.value;
                      const formattedDateTime = `${selectedDate} 23:59:59`;
                      if (filters?.quickFilter) {
                        handleFilterClick("quickFilter", "");
                      }
                      handleDateChange("end", formattedDateTime);
                    }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      max: todayDate,
                    }}
                    id="filters_section_end_date"
                    data-test-id="filters_section_end_date"
                    aria-label="End Date"
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default FiltersSection;
